import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  Phone,
  MessageSquare,
  MapPin,
  Send,
  User,
  Tag,
  PenTool,
  ChevronRight,
  Truck,
  ShieldCheck,
  RotateCcw,
  Headphones,
  CheckCircle2,
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { BACKEND_URL } from '../services/api';

const Contact = () => {
  // Read logged in user from localStorage for auto-filling
  const userJson = localStorage.getItem('user');
  let loggedInUser = null;
  try {
    if (userJson) loggedInUser = JSON.parse(userJson);
  } catch (e) {}

  const [formData, setFormData] = useState({
    fullName: loggedInUser?.name || '',
    email: loggedInUser?.email || '',
    subject: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: undefined });
    }
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    const errors = {};

    if (!formData.fullName || !formData.fullName.trim()) {
      errors.fullName = 'Please enter your full name.';
    }
    if (!formData.email || !formData.email.trim()) {
      errors.email = 'Please enter your email address.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!formData.subject || !formData.subject.trim()) {
      errors.subject = 'Please enter a subject.';
    }
    if (!formData.message || !formData.message.trim()) {
      errors.message = 'Please type your message.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSubmitError('Please complete all required fields highlighted in red below.');
      return;
    }

    setFormErrors({});
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          subject: formData.subject,
          message: formData.message
        })
      });
      const data = await response.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({ fullName: '', email: '', subject: '', message: '' });
      } else {
        setSubmitError(data.message || "Failed to send message. Please try again.");
      }
    } catch (err) {
      setSubmitError("Network error. Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fafbfc', padding: '40px 24px', fontFamily: "'Plus Jakarta Sans', sans-serif", width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        
        {/* 1. BREADCRUMBS & TOP TITLE HEADER */}
        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '24px' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>&gt;</span>
          <span style={{ color: '#0f172a', fontWeight: 600 }}>Contact Us</span>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '600px', margin: '0 auto 48px auto' }}>
          <h1 style={{ fontSize: '42px', fontWeight: 900, color: '#0f172a', letterSpacing: '-0.03em', margin: '0 0 10px 0' }}>
            We’re Here to <span style={{ color: '#dc2626' }}>Help</span>
          </h1>
          <p style={{ fontSize: '15px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
            Have a question? Need support? Our team is ready to assist you.
          </p>
        </div>

        {/* 2. MAIN SPLIT GRID: LEFT FORM (7fr) vs RIGHT CONTACT INFO (5fr) MATCHING contact page.png 1:1 */}
        <div className="cart-page-grid" style={{ gridTemplateColumns: '1fr 420px', gap: '32px', marginBottom: '40px' }}>
          
          {/* LEFT COLUMN: SEND US A MESSAGE FORM */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '36px', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>
              Send Us a Message
            </h2>
            <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 28px 0' }}>
              Fill out the form below and we'll get back to you as soon as possible.
            </p>

            {submitted && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CheckCircle2 size={20} color="#16a34a" />
                <div style={{ fontSize: '13.5px', color: '#15803d', fontWeight: 700 }}>
                  Thank you! Your message has been sent successfully. We'll reply within 24 hours.
                </div>
              </div>
            )}

            {submitError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <AlertCircle size={20} color="#dc2626" />
                <div style={{ fontSize: '13.5px', color: '#b91c1c', fontWeight: 700 }}>
                  {submitError}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Row 1: Full Name & Email Address */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: formErrors.fullName ? '#dc2626' : '#0f172a', marginBottom: '8px' }}>
                    Full Name <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <User size={16} color={formErrors.fullName ? '#dc2626' : '#94a3b8'} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      style={{
                        width: '100%',
                        padding: '12px 14px 12px 42px',
                        borderRadius: '12px',
                        border: formErrors.fullName ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        fontSize: '13.5px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        background: formErrors.fullName ? '#fff5f5' : '#ffffff'
                      }}
                    />
                  </div>
                  {formErrors.fullName && (
                    <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.fullName}</span>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: formErrors.email ? '#dc2626' : '#0f172a', marginBottom: '8px' }}>
                    Email Address <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <Mail size={16} color={formErrors.email ? '#dc2626' : '#94a3b8'} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      style={{
                        width: '100%',
                        padding: '12px 14px 12px 42px',
                        borderRadius: '12px',
                        border: formErrors.email ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                        fontSize: '13.5px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        background: formErrors.email ? '#fff5f5' : '#ffffff'
                      }}
                    />
                  </div>
                  {formErrors.email && (
                    <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.email}</span>
                  )}
                </div>
              </div>

              {/* Row 2: Subject */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: formErrors.subject ? '#dc2626' : '#0f172a', marginBottom: '8px' }}>
                  Subject <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <Tag size={16} color={formErrors.subject ? '#dc2626' : '#94a3b8'} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                    style={{
                      width: '100%',
                      padding: '12px 14px 12px 42px',
                      borderRadius: '12px',
                      border: formErrors.subject ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                      fontSize: '13.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      background: formErrors.subject ? '#fff5f5' : '#ffffff'
                    }}
                  />
                </div>
                {formErrors.subject && (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.subject}</span>
                )}
              </div>

              {/* Row 3: Message Textarea */}
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 800, color: formErrors.message ? '#dc2626' : '#0f172a', marginBottom: '8px' }}>
                  Message <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <PenTool size={16} color={formErrors.message ? '#dc2626' : '#94a3b8'} style={{ position: 'absolute', left: '14px', top: '16px' }} />
                  <textarea
                    name="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Type your message here..."
                    style={{
                      width: '100%',
                      padding: '14px 14px 14px 42px',
                      borderRadius: '12px',
                      border: formErrors.message ? '1.5px solid #dc2626' : '1px solid #cbd5e1',
                      fontSize: '13.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                      background: formErrors.message ? '#fff5f5' : '#ffffff',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
                {formErrors.message && (
                  <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px', display: 'block' }}>{formErrors.message}</span>
                )}
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: '#dc2626',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '12px 28px',
                    fontSize: '14px',
                    fontWeight: 800,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 14px rgba(220, 38, 38, 0.3)'
                  }}
                >
                  <span>{submitting ? 'Sending...' : 'Send Message'}</span>
                  <Send size={15} />
                </button>
              </div>

            </form>
          </div>

          {/* RIGHT COLUMN: CONTACT INFORMATION CARD MATCHING contact page.png 1:1 */}
          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '24px', padding: '36px', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>
              Contact Information
            </h2>
            <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 28px 0' }}>
              Reach out to us through any of the channels below.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {/* Channel 1: Email Us */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Mail size={18} color="#dc2626" />
                </div>
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email Us</div>
                  <div style={{ fontSize: '14.5px', fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>Info@infraedgeredlight.co.uk</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>We reply within 24 hours</div>
                </div>
              </div>

              {/* Channel 2: Call Us */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Phone size={18} color="#dc2626" />
                </div>
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Call Us</div>
                  <div style={{ fontSize: '14.5px', fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>0121 455 6529</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Mon - Fri: 9:00 AM - 6:00 PM (GMT)</div>
                </div>
              </div>

              {/* Channel 3: Live Chat */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', paddingBottom: '20px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MessageSquare size={18} color="#dc2626" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Live Chat</div>
                  <div style={{ fontSize: '14.5px', fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>Chat with our support team</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Available on our website</div>
                </div>
                <ChevronRight size={18} color="#94a3b8" style={{ marginTop: '12px' }} />
              </div>

              {/* Channel 4: Address */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#fef2f2', border: '1px solid #fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MapPin size={18} color="#dc2626" />
                </div>
                <div>
                  <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Address</div>
                  <div style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>InfraEdge Red Light Therapy</div>
                  <div style={{ fontSize: '12.5px', color: '#64748b', lineHeight: 1.4 }}>
                    273 Hagley Road Edgbaston,<br />
                    Birmingham B16 9NB,<br />
                    United Kingdom
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* 3. BOTTOM BANNER CARD MATCHING contact page.png 1:1 */}
        <div
          style={{
            background: 'linear-gradient(135deg, #fff5f5 0%, #ffffff 100%)',
            border: '1px solid #fecaca',
            borderRadius: '24px',
            padding: '36px 44px',
            marginBottom: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '32px',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '28px', flex: 1, minWidth: '280px' }}>
            <img
              src="/panel_144led_pro_1787122673869.jpg"
              alt="InfraEdge Panel"
              style={{ width: '90px', height: '110px', objectFit: 'contain' }}
            />
            <div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#64748b', marginBottom: '4px' }}>Still have questions?</div>
              <h3 style={{ fontSize: '22px', fontWeight: 900, color: '#0f172a', margin: '0 0 6px 0' }}>
                Our support team is <span style={{ color: '#dc2626' }}>here for you</span>.
              </h3>
              <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0, lineHeight: 1.5, maxWidth: '520px' }}>
                Whether you need help choosing the right panel or have questions about your order, we're just a message away.
              </p>
            </div>
          </div>

          <Link
            to="/faq"
            style={{
              background: '#ffffff',
              border: '1px solid #fca5a5',
              color: '#dc2626',
              borderRadius: '9999px',
              padding: '12px 28px',
              fontSize: '13.5px',
              fontWeight: 800,
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.08)'
            }}
          >
            <span>Visit FAQ Page</span>
            <ArrowRight size={15} />
          </Link>
        </div>

        {/* 4. VALUE PROPOSITION 4 BADGES BAR */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '20px', padding: '24px 32px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Truck size={18} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a' }}>Free Shipping</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>On all orders</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ShieldCheck size={18} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a' }}>2 Year Warranty</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Peace of mind</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <RotateCcw size={18} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a' }}>60 Day Returns</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Hassle free</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Headphones size={18} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 800, color: '#0f172a' }}>Dedicated Support</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>We're here to help</div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};

export default Contact;
